/**
 * Popsie Fish Co — Combined Cut + Box Size Tier Picker
 * -----------------------------------------------------
 * Drives the two-dimensional variant decision (Cut Type × Box Size) for the
 * merged Sockeye product. Bridges custom cut toggle + size cards into Dawn's
 * native variant-selects pipeline.
 *
 * Dawn computes the active variant from the `selected` ATTRIBUTE on the
 * <option>s inside <variant-selects> (see VariantSelects.selectedOptionValues
 * in global.js). So selecting a variant means: set the selected option in BOTH
 * the cut <select> and the size <select>, then dispatch one `change` so Dawn
 * fetches the new section HTML (price / media / availability).
 *
 * The custom UI (.popsie-tier-picker--combined) lives OUTSIDE <variant-selects>,
 * so Dawn's AJAX re-render never replaces it. We use delegated listeners and
 * re-query the (replaced) selects on every interaction.
 *
 * Scoped strictly to .popsie-tier-picker--combined so it never collides with
 * the single-dimension popsie-tier-picker.js (which is not loaded on this page).
 */
(function () {
  'use strict';

  if (window.PopsieCombinedTierPicker && typeof window.PopsieCombinedTierPicker.initAll === 'function') {
    window.PopsieCombinedTierPicker.initAll();
    return;
  }

  var NUDGE_ICON_UP = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>';
  var NUDGE_ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  var stickyBars = {};

  // ── Cart-aware progress banner (ported from popsie-tier-picker.js) ──────────
  function updateProgressBanner(picker, selectedWeight) {
    var banner = picker.querySelector('.popsie-pdp-cart-progress-banner');
    if (!banner) return;

    var cartWeight = parseFloat(banner.dataset.cartWeight || 0);
    var totalPotential = cartWeight + selectedWeight;
    var cartPct = Math.min((cartWeight / 20) * 100, 100);
    var potentialPct = Math.min((totalPotential / 20) * 100, 100);

    var preview = banner.querySelector('.popsie-progress-preview');
    if (preview) {
      preview.style.left = cartPct + '%';
      preview.style.width = Math.max(potentialPct - cartPct, 0) + '%';
    }

    banner.querySelectorAll('.popsie-progress-mark').forEach(function (mark) {
      var lb = parseFloat(mark.dataset.lb || 0);
      mark.classList.toggle('reached', totalPotential >= lb);
    });

    var isMax = totalPotential >= 20;
    var nudge = banner.querySelector('.popsie-progress-banner-nudge');
    if (nudge) {
      nudge.classList.toggle('is-max', isMax);
      var chip = nudge.querySelector('.popsie-progress-nudge-chip');
      if (chip) chip.innerHTML = isMax ? NUDGE_ICON_CHECK : NUDGE_ICON_UP;
    }

    var nudgeText = banner.querySelector('.popsie-pdp-nudge-text');
    if (nudgeText) {
      var html = '';
      if (totalPotential < 5) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (5 - totalPotential) + ' lb</strong> more to reach the 5 lb Starter box milestone.';
      } else if (totalPotential < 10) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (10 - totalPotential) + ' lb</strong> more to reach the 10 lb Value box milestone.';
      } else if (totalPotential < 15) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (15 - totalPotential) + ' lb</strong> more to reach the 15 lb Family box milestone.';
      } else if (totalPotential < 20) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (20 - totalPotential) + ' lb</strong> more to reach the 20 lb Freezer box milestone.';
      } else {
        html = 'Adding this ' + selectedWeight + ' lb fills your box to <strong>' + totalPotential + ' lbs</strong>. Your box is stocked past the 20 lb Freezer milestone.';
      }
      nudgeText.innerHTML = html;
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function getPicker(el) {
    return el ? el.closest('.popsie-tier-picker--combined') : null;
  }

  function visibleCardsForCut(picker, cut) {
    return Array.prototype.slice.call(
      picker.querySelectorAll('.popsie-tier-card')
    ).filter(function (c) { return c.dataset.cut === cut; });
  }

  // Write the chosen variant id straight into the add-to-cart form(s).
  //
  // Dawn only updates the hidden `id` input after a network round-trip
  // (select change -> section fetch -> updateVariantInputs). If that trip is
  // slow, aborted, or throws, the card the customer sees ("20 lb") and the
  // variant the form submits ("5 lb") silently diverge. Writing the id here,
  // synchronously, on every selection closes that gap; the fetch then only
  // refreshes price/media and (re)confirms the same id.
  function syncFormVariant(sectionId, variantId) {
    if (!variantId) return;
    ['product-form-', 'product-form-installment-'].forEach(function (prefix) {
      var form = document.getElementById(prefix + sectionId);
      if (!form) return;
      var input = form.querySelector('input[name="id"]');
      if (!input) return;
      input.disabled = false;
      if (input.value !== variantId) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // Set the <option selected> attribute (Dawn reads the attribute, not .value)
  // for whichever select contains an option with this option-value-id.
  function setSelectByOvid(variantSelects, ovid) {
    if (!ovid) return null;
    var selects = variantSelects.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      var select = selects[s];
      for (var i = 0; i < select.options.length; i++) {
        var opt = select.options[i];
        if (opt.dataset.optionValueId === ovid) {
          for (var j = 0; j < select.options.length; j++) {
            select.options[j].removeAttribute('selected');
            select.options[j].selected = false;
          }
          opt.setAttribute('selected', 'selected');
          opt.selected = true;
          select.selectedIndex = i;
          return select;
        }
      }
    }
    return null;
  }

  // ── UI sync (summary, sticky bar, what-you-get strip, toggle state) ─────────
  function updateBoxSummary(picker, card) {
    var valueEl = picker.querySelector('[data-summary-value]');
    var sticky = document.querySelector('.popsie-sticky-atc[data-section-id="' + picker.dataset.sectionId + '"]');
    if (!card) return;

    var cut = card.dataset.cut;
    var weight = card.dataset.weight;
    var perlb = card.dataset.perlb;
    var price = card.dataset.price;
    var isPortion = card.dataset.isPortion === 'true';
    var portions = card.dataset.portions;

    var parts = [cut, weight + ' lb box'];
    if (perlb) parts.push(perlb);
    if (isPortion && portions && portions !== '0') parts.push(portions + ' portions');
    if (isPortion && card.dataset.perServing) parts.push(card.dataset.perServing + '/serving');
    parts.push(price);
    var text = parts.join(' · ');

    if (valueEl) valueEl.textContent = text;

    if (sticky) {
      var meta = sticky.querySelector('[data-sticky-meta]');
      if (meta) meta.textContent = cut + ' · ' + weight + ' lb · ' + price;
      var addBtn = sticky.querySelector('[data-sticky-add]');
      if (addBtn) {
        var soldOut = card.classList.contains('sold-out');
        addBtn.dataset.soldOut = soldOut ? 'true' : 'false';
        addBtn.disabled = soldOut || sticky.getAttribute('aria-hidden') !== 'false';
      }
    }
  }

  function updateWyg(picker, cut) {
    var productSection = picker.closest('product-info') || picker;
    productSection.querySelectorAll('.popsie-wyg').forEach(function (w) {
      w.hidden = (w.dataset.cut !== cut);
    });
  }

  function setCutToggleState(picker, cut) {
    picker.querySelectorAll('.popsie-cut-option').forEach(function (btn) {
      var active = btn.dataset.cut === cut;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
      btn.setAttribute('tabindex', active ? '0' : '-1');
    });
  }

  // ── Core: select a specific variant card ────────────────────────────────────
  function selectCard(card, opts) {
    opts = opts || {};
    if (!card || card.classList.contains('sold-out')) return;

    var picker = getPicker(card);
    if (!picker) return;

    var cut = card.dataset.cut;
    var sectionId = picker.dataset.sectionId;

    // Visual state across the whole grid (roving tabindex on visible cards).
    picker.querySelectorAll('.popsie-tier-card').forEach(function (c) {
      c.classList.remove('active');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add('active');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');
    var radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    // Keep the cut toggle + copy strip in sync with the chosen card's cut.
    setCutToggleState(picker, cut);
    updateWyg(picker, cut);

    updateProgressBanner(picker, parseFloat(card.dataset.weight || 0));
    updateBoxSummary(picker, card);

    if (opts.focus) card.focus();

    // The form must carry this card's variant id from this moment on —
    // independent of whether Dawn's section fetch below ever completes.
    syncFormVariant(sectionId, card.dataset.variantId);

    // Drive Dawn: set both option selects, then fire one change.
    var variantSelects = document.querySelector('#variant-selects-' + sectionId);
    if (!variantSelects) return;
    var sizeSelect = setSelectByOvid(variantSelects, card.dataset.sizeOvid);
    setSelectByOvid(variantSelects, card.dataset.cutOvid);
    var changeTarget = sizeSelect || variantSelects.querySelector('select');
    if (changeTarget) {
      changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── Switch cut: filter grid, then pick a sensible size in the new cut ───────
  function selectCut(btn, opts) {
    opts = opts || {};
    var picker = getPicker(btn);
    if (!picker) return;
    var cut = btn.dataset.cut;

    // Reveal this cut's cards, hide the others.
    picker.querySelectorAll('.popsie-tier-card').forEach(function (c) {
      c.hidden = (c.dataset.cut !== cut);
    });
    setCutToggleState(picker, cut);
    updateWyg(picker, cut);

    var cards = visibleCardsForCut(picker, cut);
    var available = cards.filter(function (c) { return !c.classList.contains('sold-out'); });

    // Prefer keeping the same box weight; else first available; else nothing.
    var prevActive = picker.querySelector('.popsie-tier-card.active');
    var prevWeight = prevActive ? prevActive.dataset.weight : null;
    var target = null;
    if (prevWeight) {
      target = available.filter(function (c) { return c.dataset.weight === prevWeight; })[0] || null;
    }
    if (!target) target = available[0] || null;

    if (target) {
      selectCard(target, { focus: !!opts.focus });
    } else {
      // Whole cut sold out: still reflect the toggle + copy, keep CTA honest.
      if (opts.focus) btn.focus();
    }
  }

  // ── Delegated click handling ────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    // A click on the <label> card also fires a synthetic click on its hidden
    // radio; handle the card once, not twice (each pass kicks off a fetch).
    if (e.target.tagName === 'INPUT') return;
    var cutBtn = e.target.closest('.popsie-cut-option');
    if (cutBtn && getPicker(cutBtn)) {
      // A fully sold-out cut has no selectable size; switching to it would
      // leave the form pointing at the previously chosen (now hidden) card.
      if (cutBtn.classList.contains('cut-unavailable')) return;
      selectCut(cutBtn);
      return;
    }
    var card = e.target.closest('.popsie-tier-card');
    if (card && getPicker(card)) {
      selectCard(card);
    }
  });

  // ── Keyboard: roving tabindex for both the cut toggle and the size grid ─────
  function siblingGroup(el) {
    var picker = getPicker(el);
    if (!picker) return null;
    if (el.classList.contains('popsie-cut-option')) {
      return Array.prototype.slice.call(picker.querySelectorAll('.popsie-cut-option'));
    }
    var cut = el.dataset.cut;
    return visibleCardsForCut(picker, cut);
  }

  document.addEventListener('keydown', function (e) {
    var el = e.target.closest('.popsie-cut-option, .popsie-tier-card');
    if (!el || !getPicker(el)) return;

    var key = e.key;
    var isArrow = (key === 'ArrowLeft' || key === 'ArrowUp' || key === 'ArrowRight' || key === 'ArrowDown');
    var isActivate = (key === 'Enter' || key === ' ');
    if (!isArrow && !isActivate) return;
    e.preventDefault();

    var isCut = el.classList.contains('popsie-cut-option');

    if (isActivate) {
      if (isCut) selectCut(el, { focus: true });
      else selectCard(el, { focus: true });
      return;
    }

    var group = siblingGroup(el);
    if (!group || !group.length) return;
    var idx = group.indexOf(el);
    if (idx === -1) return;
    var dir = (key === 'ArrowRight' || key === 'ArrowDown') ? 1 : -1;
    var len = group.length;
    var next = idx;
    for (var step = 0; step < len; step++) {
      next = (next + dir + len) % len;
      var candidate = group[next];
      if (candidate.classList.contains('sold-out') || candidate.classList.contains('cut-unavailable')) continue;
      if (isCut) selectCut(candidate, { focus: true });
      else selectCard(candidate, { focus: true });
      return;
    }
  });

  // ── Mobile sticky add-to-cart ───────────────────────────────────────────────
  function setStickyVisibility(sticky, show) {
    var addBtn = sticky.querySelector('[data-sticky-add]');

    if (!show && sticky.contains(document.activeElement) && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) sticky.removeAttribute('inert');
    else sticky.setAttribute('inert', '');

    if (addBtn) {
      addBtn.disabled = !show || addBtn.dataset.soldOut === 'true';
    }
  }

  function cleanupStickyBar(sectionId, preserveSticky) {
    sectionId = String(sectionId || '');
    var entry = stickyBars[sectionId];

    if (entry) {
      if (entry.observer) entry.observer.disconnect();
      if (entry.addBtn && entry.clickHandler) {
        entry.addBtn.removeEventListener('click', entry.clickHandler);
      }
      if (entry.sticky !== preserveSticky && entry.sticky.isConnected) entry.sticky.remove();
      delete stickyBars[sectionId];
    }

    document.querySelectorAll('.popsie-sticky-atc[data-section-id]').forEach(function (sticky) {
      if (sticky.dataset.sectionId === sectionId && sticky !== preserveSticky) sticky.remove();
    });
  }

  function initStickyBar(picker) {
    var sticky = picker.querySelector('.popsie-sticky-atc');
    if (!sticky) return;

    var sectionId = sticky.dataset.sectionId;
    cleanupStickyBar(sectionId, sticky);
    sticky.dataset.initialized = 'true';
    setStickyVisibility(sticky, false);

    // Relocate to <body> so position:fixed escapes any transformed/overflow ancestor.
    document.body.appendChild(sticky);

    var addBtn = sticky.querySelector('[data-sticky-add]');
    var clickHandler = null;
    if (addBtn) {
      clickHandler = function () {
        var realBtn = document.getElementById('ProductSubmitButton-' + sectionId);
        if (realBtn && !realBtn.hasAttribute('disabled')) realBtn.click();
      };
      addBtn.addEventListener('click', clickHandler);
    }

    var entry = {
      sticky: sticky,
      addBtn: addBtn,
      clickHandler: clickHandler,
      observer: null
    };
    stickyBars[sectionId] = entry;

    // Show the bar only once the real Add-to-cart button has scrolled out of view.
    var anchor = document.getElementById('ProductSubmitButton-' + sectionId) ||
                 document.querySelector('#variant-selects-' + sectionId);
    if (!anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var below = entry.boundingClientRect.top > 0;
        var show = !entry.isIntersecting && !below; // scrolled past the top
        setStickyVisibility(sticky, show);
      });
    }, { threshold: 0 });
    observer.observe(anchor);
    entry.observer = observer;
  }

  // Move the cut-aware "what you get" strip to just below the buy buttons.
  function relocateWyg(picker) {
    var wyg = picker.querySelector('.popsie-wyg-wrap');
    if (!wyg || wyg.dataset.relocated) return;
    var form = document.getElementById('product-form-' + picker.dataset.sectionId);
    var anchor = form ? (form.closest('product-form') || form) : null;
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(wyg, anchor.nextSibling);
      wyg.dataset.relocated = 'true';
    }
  }

  // Parse box weight (lb) from a cart line's variant title.
  function lineWeight(title) {
    if (!title || title.indexOf('lb') === -1) return 0;
    var parts = title.split('lb')[0].trim().split(/\s+/);
    return parseFloat(parts[parts.length - 1]) || 0;
  }

  // Live-refresh the box-progress banner's cart weight when the cart changes.
  function refreshProgressFromCart() {
    var banners = document.querySelectorAll('.popsie-tier-picker--combined .popsie-pdp-cart-progress-banner');
    if (!banners.length) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lb = 0;
        (cart.items || []).forEach(function (it) {
          if (/sockeye|salmon|halibut|cod|sablefish/i.test(it.handle || '')) {
            lb += lineWeight(it.variant_title || it.title || '') * (it.quantity || 1);
          }
        });
        document.querySelectorAll('.popsie-tier-picker--combined').forEach(function (picker) {
          var banner = picker.querySelector('.popsie-pdp-cart-progress-banner');
          if (!banner) return;
          banner.dataset.cartWeight = lb;
          var cur = banner.querySelector('.popsie-progress-banner-current strong');
          if (cur) cur.textContent = lb;
          var fill = banner.querySelector('.popsie-progress-fill');
          if (fill) fill.style.width = Math.min((lb / 20) * 100, 100) + '%';
          var active = picker.querySelector('.popsie-tier-card.active');
          updateProgressBanner(picker, active ? parseFloat(active.dataset.weight || 0) : 0);
        });
      })
      .catch(function () {});
  }

  // ── Init ────────────────────────────────────────────────────────────────────
  function initPicker(picker) {
    var active = picker.querySelector('.popsie-tier-card.active:not([hidden])') ||
                 picker.querySelector('.popsie-tier-card.active');

    // Reconcile a click that landed before this script ran (the <label>
    // still checks its radio natively) or a browser-restored radio state:
    // the checked radio is the customer's latest choice, so honour it.
    var checkedRadio = picker.querySelector('.popsie-tier-card input[type="radio"]:checked');
    var checkedCard = checkedRadio ? checkedRadio.closest('.popsie-tier-card') : null;
    if (checkedCard && checkedCard !== active && !checkedCard.classList.contains('sold-out')) {
      picker.querySelectorAll('.popsie-tier-card').forEach(function (c) {
        c.hidden = (c.dataset.cut !== checkedCard.dataset.cut);
      });
      selectCard(checkedCard);
      active = checkedCard;
    } else if (active) {
      updateProgressBanner(picker, parseFloat(active.dataset.weight || 0));
      updateBoxSummary(picker, active);
      updateWyg(picker, active.dataset.cut);
      // Make sure the form already carries the visibly selected variant.
      syncFormVariant(picker.dataset.sectionId, active.dataset.variantId);
    }
    relocateWyg(picker);
    initStickyBar(picker);
  }

  function initAll() {
    document.querySelectorAll('.popsie-tier-picker--combined').forEach(initPicker);
  }

  window.PopsieCombinedTierPicker = { initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.detail && event.detail.sectionId) cleanupStickyBar(event.detail.sectionId);
  });

  // Dawn publishes cartUpdate whenever the cart changes; keep the on-page bar in sync.
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshProgressFromCart);
  }
})();
